import { MaterialCommunityIcons } from "@expo/vector-icons";
import type { PropsWithChildren } from "react";
import React, { createContext, useContext, useState } from "react";
import { Platform, Pressable, StyleSheet, Text, View } from "react-native";
import type { StyleProp, TextStyle, ViewStyle } from "react-native";

import { useCustomTheme } from "../hooks/useCustomTheme";

export type ThemedTableHeaderColumn = {
  /**
   * Determines the relative width of the column using React Native's `flex` property.
   * Higher values make the column wider compared to others.
   * Example: [{ label: "A", width: 1 }, { label: "B", width: 2 }] makes column B twice as wide as A.
   */
  width: number;
} & (
  | {
      label: string;
      parts?: undefined;
      style?: StyleProp<TextStyle>;
    }
  | {
      label?: undefined;
      style?: StyleProp<TextStyle>; // style for each cell of the column
      parts: TextPart[]; // part.style is used for the text part in the header cell only, may be used to overwrite ThemedTableHeaderColumn.style
    }
);

type ThemedTableProps = {
  headerColumns: ThemedTableHeaderColumn[];
  /** Gives every second row a grey background. */
  striped?: boolean;
  /** Overrides the default minimum row height of 91. */
  rowMinHeight?: number;
};

type TextPart = {
  label: string;
  style?: TextStyle;
};

type SimpleTextColumn = TextPart & {
  type: "text";
};

type MultiTextColumn = {
  type: "text";
  parts: TextPart[];
};

type ActionColumn = {
  type: "action";
  actions: {
    label: string;
    onPress: () => void;
    icon?: React.ComponentProps<typeof MaterialCommunityIcons>["name"];
    disabled?: boolean;
  }[];
};

/**
 * Both SimpleTextColumn and MultiTextColumn inherit the styles from their column header,
 * but may also overwrite these column header styles with their own style prop.
 */
export type ThemedTableColumn = Readonly<
  SimpleTextColumn | MultiTextColumn | ActionColumn
>;

export type ThemedTableRowProps = Readonly<{
  columns: ThemedTableColumn[];
  testID?: string;
}>;

type InternalThemedTableRowProps = ThemedTableRowProps & {
  isFirst?: boolean;
  isLast?: boolean;
  isStriped?: boolean;
  minHeight?: number;
};

const TableContext = createContext<ThemedTableHeaderColumn[]>([]);

function getTotalColumnWidth(columns: ThemedTableHeaderColumn[]) {
  const total = columns.reduce((sum, column) => sum + column.width, 0);
  return total > 0 ? total : 1;
}

function getColumnWidthStyle(
  columnWidth: number,
  totalWidth: number,
): ViewStyle {
  const widthPercent = `${(columnWidth / totalWidth) * 100}%` as `${number}%`;
  return {
    width: widthPercent,
    maxWidth: widthPercent,
    flexBasis: widthPercent,
    flexGrow: 0,
    flexShrink: 0,
  };
}

const ThemedTableRow = ({
  columns,
  testID,
  isFirst = false,
  isLast = false,
  isStriped = false,
  minHeight,
}: InternalThemedTableRowProps) => {
  const { colors, fonts } = useCustomTheme();
  const headerColumns = useContext(TableContext);
  const totalColumnWidth = getTotalColumnWidth(headerColumns);
  const [isHovered, setIsHovered] = useState(false);
  const [hoveredActionKey, setHoveredActionKey] = useState<string | null>(null);
  const [focusedActionKey, setFocusedActionKey] = useState<string | null>(null);

  if (__DEV__) {
    validateColumns(columns, headerColumns);
  }

  const dynamicRowStyles = {
    borderColor: colors.border,
    borderTopWidth: isFirst ? 0 : 1,
    borderBottomWidth: isLast ? 1 : 0,
    borderBottomLeftRadius: isLast ? 8 : 0,
    borderBottomRightRadius: isLast ? 8 : 0,
    backgroundColor: isHovered
      ? colors.tableRowHover
      : isStriped
        ? colors.mutedBorder
        : undefined,
    ...(minHeight !== undefined && { minHeight }),
  };

  const hoverHandlers =
    Platform.OS === "web"
      ? {
          onMouseEnter: () => setIsHovered(true),
          onMouseLeave: () => setIsHovered(false),
        }
      : undefined;

  return (
    <View
      testID={testID}
      style={[styles.rowContainer, dynamicRowStyles]}
      {...(hoverHandlers as any)}
    >
      {columns.map((column, index) => {
        const width = headerColumns[index]?.width || 1;
        const columnStyle = headerColumns[index]?.style;
        const columnWidthStyle = getColumnWidthStyle(width, totalColumnWidth);

        if (column.type === "text") {
          return (
            <View
              key={`text-${index}`}
              style={[
                styles.columnContainer,
                columnWidthStyle,
                styles.cellContainer,
              ]}
            >
              {isMultiTextColumn(column) ? (
                <View style={styles.cellTextContainer}>
                  {column.parts.map((part) => (
                    <Text
                      key={part.label}
                      style={[
                        styles.cellText,
                        styles.paddingLeft,
                        fonts.regular,
                        columnStyle,
                        { color: colors.text },
                        part.style,
                      ]}
                    >
                      {part.label}
                    </Text>
                  ))}
                </View>
              ) : (
                <Text
                  style={[
                    styles.cellText,
                    styles.paddingLeft,
                    fonts.regular,
                    columnStyle,
                    { color: colors.text },
                    column.style,
                  ]}
                >
                  {column.label}
                </Text>
              )}
            </View>
          );
        } else if (column.type === "action") {
          return (
            <View
              key={`action-${index}`}
              style={[
                styles.columnContainer,
                styles.actionContainer,
                styles.paddingLeft,
                columnWidthStyle,
              ]}
            >
              {column.actions.map((action, actionIndex) => {
                const actionKey = `${index}-${actionIndex}`;
                const isDisabled = Boolean(action.disabled);
                const borderColor = isDisabled
                  ? "transparent"
                  : focusedActionKey === actionKey
                    ? colors.palette.spinach30
                    : hoveredActionKey === actionKey
                      ? colors.text
                      : "transparent";

                return (
                  <Pressable
                    key={`${action.label}-${actionIndex}`}
                    disabled={isDisabled}
                    focusable={!isDisabled}
                    aria-disabled={isDisabled}
                    onHoverIn={() => {
                      if (isDisabled) return;
                      setHoveredActionKey(actionKey);
                    }}
                    onHoverOut={() => {
                      setHoveredActionKey((current) =>
                        current === actionKey ? null : current,
                      );
                    }}
                    onFocus={() => {
                      if (isDisabled) return;
                      setFocusedActionKey(actionKey);
                    }}
                    onBlur={() => {
                      setFocusedActionKey((current) =>
                        current === actionKey ? null : current,
                      );
                    }}
                    onPress={action.onPress}
                    style={[
                      styles.actionButton,
                      isDisabled && styles.actionButtonDisabled,
                      Platform.OS === "web"
                        ? ({ outlineStyle: "none" } as any)
                        : undefined,
                      { borderColor },
                    ]}
                  >
                    {action.icon && (
                      <MaterialCommunityIcons name={action.icon} size={20} />
                    )}
                    <Text style={[styles.actionText, fonts.bold]}>
                      {action.label}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          );
        }
        return null;
      })}
    </View>
  );
};

function isMultiTextColumn(
  column: ThemedTableColumn,
): column is Readonly<MultiTextColumn> {
  return "parts" in column && Array.isArray(column.parts);
}

function validateColumns(
  rowColumns: readonly ThemedTableColumn[],
  headerColumns: ThemedTableHeaderColumn[],
) {
  if (rowColumns.length !== headerColumns.length) {
    console.warn(
      `Mismatch in number of columns: row has ${rowColumns.length}, header has ${headerColumns.length}`,
    );
  }
}

export const ThemedTable = ({
  headerColumns,
  striped = false,
  rowMinHeight,
  children,
}: PropsWithChildren<ThemedTableProps>) => {
  const { colors } = useCustomTheme();
  const childrenArray = React.Children.toArray(children);

  return (
    <TableContext.Provider value={headerColumns}>
      <View
        style={[
          styles.tableContainer,
          {
            borderColor: colors.border,
          },
        ]}
      >
        <TableHeader columns={headerColumns} />
        {childrenArray.map((child, index, arr) => {
          if (React.isValidElement<InternalThemedTableRowProps>(child)) {
            return React.cloneElement(child, {
              ...child.props,
              isFirst: index === 0,
              isLast: index === arr.length - 1,
              isStriped: striped && index % 2 === 1,
              minHeight: rowMinHeight,
              key: child.key || index,
            });
          }
          return child;
        })}
      </View>
    </TableContext.Provider>
  );
};

function TableHeader({ columns }: { columns: ThemedTableHeaderColumn[] }) {
  const { colors } = useCustomTheme();
  const totalColumnWidth = getTotalColumnWidth(columns);
  return (
    <View style={[styles.headerContainer, { backgroundColor: colors.border }]}>
      {columns.map((headerColumn) => {
        const key =
          headerColumn.label ||
          headerColumn.parts!.map((part) => part.label).join("-");
        const columnWidthStyle = getColumnWidthStyle(
          headerColumn.width,
          totalColumnWidth,
        );
        return (
          <View key={key} style={[styles.columnContainer, columnWidthStyle]}>
            {"parts" in headerColumn && headerColumn.parts ? (
              <View style={[styles.headerColumnContainer, styles.paddingLeft]}>
                {headerColumn.parts.map((part) => (
                  <Text
                    key={part.label}
                    style={[
                      styles.defaultHeaderTextStyle,
                      { color: colors.background },
                      part.style,
                    ]}
                  >
                    {part.label}
                  </Text>
                ))}
              </View>
            ) : (
              <Text
                style={[
                  styles.defaultHeaderTextStyle,
                  styles.paddingLeft,
                  { color: colors.background },
                  headerColumn.style,
                ]}
              >
                {headerColumn.label}
              </Text>
            )}
          </View>
        );
      })}
    </View>
  );
}

ThemedTable.Row = ThemedTableRow;

const styles = StyleSheet.create({
  tableContainer: {
    width: "100%",
    borderRadius: 8,
    borderLeftWidth: 1,
    borderRightWidth: 1,
  },
  headerContainer: {
    flexDirection: "row",
    height: 58,
    alignItems: "center",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
  },
  defaultHeaderTextStyle: {
    fontFamily: "PoppinsRegular",
    fontSize: 15,
    lineHeight: 18,
  },
  columnContainer: {
    minWidth: 0,
    overflow: "hidden",
  },
  rowContainer: {
    flexDirection: "row",
    minHeight: 91,
    alignItems: "center",
    paddingVertical: 8,
  },
  cellText: {
    fontSize: 16,
    lineHeight: 20.8,
    flexShrink: 1,
    // CSS properties for intelligent word breaking (Expo Web only)
    ...(Platform.OS === "web" && {
      wordBreak: "break-word",
      hyphens: "auto",
      overflowWrap: "break-word",
    }),
  },
  cellContainer: {
    justifyContent: "center",
  },
  cellTextContainer: {
    flexDirection: "column",
  },
  actionContainer: {
    gap: 8,
    justifyContent: "center",
    alignItems: "flex-start",
  },
  actionButton: {
    flexDirection: "row",
    gap: 8,
    alignItems: "center",
    borderWidth: 2,
    borderRadius: 4,
    alignSelf: "flex-start",
    paddingHorizontal: 4,
  },
  actionButtonDisabled: {
    opacity: 0.5,
  },
  actionText: {
    fontSize: 16,
    lineHeight: 20.8,
  },
  paddingLeft: {
    paddingLeft: 16,
  },
  headerColumnContainer: {
    width: "100%",
  },
});
